/**
 * PostHog wrapper.
 *
 * Per https://posthog.com/docs/libraries/react. The package is now a
 * direct dependency (`posthog-js`, which also ships the React provider
 * at `posthog-js/react`). Init happens once at module load, gated on
 * the build-time env var `VITE_POSTHOG_KEY` — without it, every export
 * is a no-op so PostHog can be left off in dev without changing
 * product code.
 *
 * Configuration:
 *   VITE_POSTHOG_KEY      (required) public project key, `phc_…`
 *   VITE_POSTHOG_HOST     (optional) api_host. Defaults to PostHog's
 *                          EU cloud. In production we point this at
 *                          the managed reverse-proxy domain (e.g.
 *                          https://n.synfol.io) so event traffic looks
 *                          first-party and isn't dropped by ad-blockers.
 *   VITE_POSTHOG_UI_HOST  (optional) ui_host. Where toolbar / shareable
 *                          links resolve to. Stays on the real PostHog
 *                          host even when api_host is proxied —
 *                          otherwise the "open in PostHog" links from
 *                          captured events would 404.
 *
 * Conventions:
 *   - We never enable PostHog's own autocapture; we feed it explicit
 *     events from the UI Instrumentation Pack (`installUiAutocapture`
 *     below). Two autocapture sources would double-count interactions.
 *   - User identity is opaque (user.id, never email or name). The
 *     PostHog distinct_id becomes user.id after `identifyUser` runs.
 *   - DNT honoured: if the browser sends Do-Not-Track, init is skipped
 *     and the module stays a no-op.
 */

import posthog from 'posthog-js';
import type { PostHog as PostHogType } from 'posthog-js';

type Maybe<T> = T | null | undefined;

const KEY = import.meta.env.VITE_POSTHOG_KEY as Maybe<string>;
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as Maybe<string>) ?? 'https://eu.i.posthog.com';
const UI_HOST =
  (import.meta.env.VITE_POSTHOG_UI_HOST as Maybe<string>) ?? 'https://eu.posthog.com';

function dntEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;
  const n = navigator as { doNotTrack?: string };
  return (
    n.doNotTrack === '1' ||
    (typeof window !== 'undefined' &&
      (window as { doNotTrack?: string }).doNotTrack === '1')
  );
}

let initialised = false;
function ensureInit(): PostHogType | null {
  if (initialised) return KEY ? posthog : null;
  initialised = true;
  if (!KEY) return null;
  if (dntEnabled()) return null;
  posthog.init(KEY, {
    api_host: HOST,
    // ui_host keeps "open in PostHog" links pointing at the real UI
    // even when api_host is a reverse proxy on our domain.
    ui_host: UI_HOST,
    // Pin to the May 2026 defaults bundle. PostHog ships new sensible
    // defaults over time; pinning to a date opts us into a known set
    // and lets us roll forward deliberately instead of silently.
    defaults: '2026-05-30',
    // Don't create person profiles for anonymous traffic — keeps the
    // person count tied to real users we've identified rather than
    // ballooning with every drive-by landing page visit.
    person_profiles: 'identified_only',
    // We capture deliberately via installUiAutocapture; PostHog's own
    // autocapture would double-emit and miss the entity context.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    persistence: 'localStorage+cookie',
  });
  return posthog;
}

/** The PostHog client (or null when KEY/DNT means it's off). */
export function getPostHog(): PostHogType | null {
  return ensureInit();
}

export function track(event: string, props?: Record<string, unknown>): void {
  ensureInit()?.capture(event, props);
}

export function identifyUser(userId: string, props?: Record<string, unknown>): void {
  ensureInit()?.identify(userId, props);
}

/**
 * Set the active workspace as a PostHog "group". Groups are how
 * PostHog attributes events to a higher-level entity (team /
 * tenant) for cohort and retention analysis. Every subsequent
 * captured event is automatically tagged with this group until
 * the next call (or resetAnalytics).
 *
 * Group properties accept name + plan-shape fields; we keep it
 * minimal — the workspace id, slug, and human name. No member
 * lists or PII.
 */
export function setWorkspaceGroup(
  tenantId: string,
  props?: { slug?: string; name?: string; isPersonal?: boolean },
): void {
  const ph = ensureInit();
  if (!ph) return;
  ph.group('workspace', tenantId, props as Record<string, unknown> | undefined);
}

export function resetAnalytics(): void {
  ensureInit()?.reset();
}

/**
 * Autocapture per UI Instrumentation Pack §9.
 *
 * Installs ONE delegated click listener on `document`. For any click
 * whose target has a `data-ui-name` ancestor, we walk the DOM upward
 * to merge entity context (per §7's algorithm) and capture a
 * `ui_interaction` event with the resolved shape. Anchor-only nodes
 * (no `data-ui-action`) are explicitly skipped — keeps the event
 * stream signal-to-noise manageable.
 *
 * Call once at app boot. Safe to call multiple times — guarded by a
 * window flag.
 */
export function installUiAutocapture(): void {
  if (typeof window === 'undefined') return;
  const w = window as { __synfolioAutocaptureInstalled?: boolean };
  if (w.__synfolioAutocaptureInstalled) return;
  w.__synfolioAutocaptureInstalled = true;

  document.addEventListener(
    'click',
    (ev) => {
      const t = ev.target;
      if (!(t instanceof Element)) return;
      const anchor = t.closest('[data-ui-name]');
      if (!anchor) return;
      const ctx = resolveContext(anchor);
      if (!ctx.action) return; // anchor-only nodes are not trackable
      ensureInit()?.capture('ui_interaction', {
        event_type: 'click',
        ui_name: ctx.name,
        entity: ctx.entity,
        entity_id: ctx.entityId,
        action: ctx.action,
        state: ctx.state,
        label: ctx.label,
        route: window.location.pathname,
        ancestor_names: ctx.ancestorNames,
      });
    },
    { capture: true },
  );
}

interface UiNodeAttrs {
  name: string;
  entity?: string;
  entityId?: string;
  action?: string;
  state?: string;
  label?: string;
}

function readAttrs(el: Element): UiNodeAttrs | null {
  const name = el.getAttribute('data-ui-name');
  if (!name) return null;
  return {
    name,
    entity: el.getAttribute('data-ui-entity') ?? undefined,
    entityId: el.getAttribute('data-ui-entity-id') ?? undefined,
    action: el.getAttribute('data-ui-action') ?? undefined,
    state: el.getAttribute('data-ui-state') ?? undefined,
    label: el.getAttribute('data-ui-label') ?? undefined,
  };
}

function resolveContext(el: Element): UiNodeAttrs & { ancestorNames: string[] } {
  const node = readAttrs(el) ?? { name: '' };
  const ancestorNames: string[] = [];
  let entity = node.entity;
  let entityId = node.entityId;
  let cursor: Element | null = el.parentElement;
  while (cursor) {
    const a = readAttrs(cursor);
    if (a) {
      ancestorNames.push(a.name);
      if (!entity && a.entity) {
        entity = a.entity;
        if (!entityId && a.entityId) entityId = a.entityId;
      }
    }
    cursor = cursor.parentElement;
  }
  return { ...node, entity, entityId, ancestorNames };
}
